import type { IconConstant } from '@tpg/grammar-web-icons';
import {
  tpgIconCastle,
  tpgIconDirectionsBus,
  tpgIconHouseSiding,
  tpgIconLocalCafe,
  tpgIconLocalPolice,
  tpgIconLocationCity,
  tpgIconOtherHouses,
  tpgIconPlace,
  tpgIconRestaurant,
  tpgIconSubway,
  tpgIconTrain,
  tpgIconWineBar,
} from '@tpg/grammar-web-icons';
import * as turf from '@turf/turf';

import type { GeocodingFeature } from '@nora-monorepo/nora-api-client';
import { isNonEmptyString } from '@nora-monorepo/shared';
import { i18n } from '@nora-monorepo/ui-shared';

const { t } = i18n.global;

export class GeoSearchIconGenerator {
  /**
   * Returns the correct icon based on feature properties
   * @param feature
   */
  static getIconFromFeatureProperties(feature: GeocodingFeature): IconConstant {
    switch (feature.properties.geocoding.type) {
      case 'city':
        return this.cityIcons(feature);
      case 'house':
        return this.houseIcons(feature);
      case 'street':
        return this.streetIcons(feature);
      case 'district':
        return this.districtIcons(feature);
      case 'state':
      case 'locality':
      case 'region':
      case 'county':
      case 'country':
        return tpgIconPlace;
    }
  }

  private static districtIcons(feature: GeocodingFeature): IconConstant {
    switch (feature.properties.geocoding.osm_value) {
      case 'isolated_dwelling':
      case 'farm':
        return tpgIconHouseSiding;
      default:
        return tpgIconPlace;
    }
  }

  private static streetIcons(feature: GeocodingFeature): IconConstant {
    switch (feature.properties.geocoding.osm_value) {
      case 'farm':
        return tpgIconHouseSiding;
      default:
        return tpgIconPlace;
    }
  }

  private static cityIcons(_feature: GeocodingFeature): IconConstant {
    return tpgIconLocationCity;
  }

  private static highwayIcons(feature: GeocodingFeature): IconConstant {
    switch (feature.properties.geocoding.osm_value) {
      case 'bus_stop':
        return tpgIconDirectionsBus;
      default:
        return tpgIconPlace;
    }
  }

  private static houseIcons(feature: GeocodingFeature): IconConstant {
    switch (feature.properties.geocoding.osm_key) {
      case 'office':
        return this.officeIcons(feature);
      case 'railway':
        return this.railwayIcons(feature);
      case 'amenity':
        return this.amenityIcons(feature);
      case 'highway':
        return this.highwayIcons(feature);
      default:
        return tpgIconPlace;
    }
  }

  private static officeIcons(feature: GeocodingFeature): IconConstant {
    // Should probably reconsider using castle icon for government buildings :P
    return feature.properties.geocoding.osm_value === 'government' ? tpgIconCastle : tpgIconOtherHouses;
  }

  private static amenityIcons(feature: GeocodingFeature): IconConstant {
    switch (feature.properties.geocoding.osm_value) {
      case 'bar':
        return tpgIconWineBar;
      case 'restaurant':
        return tpgIconRestaurant;
      case 'bus_station':
        return tpgIconDirectionsBus;
      case 'cafe':
        return tpgIconLocalCafe;
      case 'police':
        return tpgIconLocalPolice;
      default:
        return tpgIconPlace;
    }
  }

  private static railwayIcons(feature: GeocodingFeature): IconConstant {
    switch (feature.properties.geocoding.osm_value) {
      case 'subway_entrance':
        return tpgIconSubway;
      default:
        return tpgIconTrain;
    }
  }
}

export class GeoSearchNameGenerator {
  /**
   * Generates a custom display name based on the address property of a Feature.
   * @param feature
   */
  static generateDisplayName(feature: GeocodingFeature): string {
    const displayName = this.generateDisplayNameFromFeatureProperties(feature);

    return this.addCountryIfPresent(feature, displayName);
  }

  /**
   * Adds the country to the display name if it is present in the feature properties.
   * @param feature
   * @param displayName
   * @private
   */
  private static addCountryIfPresent(feature: GeocodingFeature, displayName: string): string {
    return this.asName([displayName, feature.properties.geocoding.country]);
  }

  /**
   * Generates a display name based on feature properties.
   * @param feature
   */
  private static generateDisplayNameFromFeatureProperties(feature: GeocodingFeature): string {
    const { name, district, city, county } = feature.properties.geocoding;

    switch (feature.properties.geocoding.type) {
      case 'city':
        return this.cityNames(feature);
      case 'house':
        return this.houseNames(feature);
      case 'district':
        return this.districtNames(feature);
      case 'street':
      case 'state':
      case 'locality':
      case 'region':
      case 'county':
      case 'country':
        if ([name, district, city, county].some(value => isNonEmptyString(value))) {
          return this.asName([name, district, city, county]);
        }
        else {
          return this.noMatchName(feature);
        }
    }
  }

  /**
   * Gives a standard name when we have no properties in the Feature.
   * @param feature
   */
  private static noMatchName(feature: GeocodingFeature): string {
    const centroid = turf.centroid(feature.geometry);
    const [longitude, latitude] = centroid.geometry.coordinates;

    return `${latitude} ${longitude}`;
  }

  private static districtNames(feature: GeocodingFeature): string {
    const { name, district, county } = feature.properties.geocoding;
    switch (feature.properties.geocoding.osm_value) {
      case 'administrative':
        return this.asName([name]);
      default:
        return this.asName([name, district, county]);
    }
  }

  private static cityNames(feature: GeocodingFeature): string {
    const { name, district, county } = feature.properties.geocoding;
    switch (feature.properties.geocoding.osm_value) {
      case 'administrative':
        return this.asName([name]);
      default:
        return this.asName([name, district, county]);
    }
  }

  private static houseNames(feature: GeocodingFeature): string {
    const { name, street, city, county } = feature.properties.geocoding;
    switch (feature.properties.geocoding.osm_key) {
      case 'railway':
        switch (feature.properties.geocoding.osm_value) {
          case 'subway_entrance':
            return this.asName([`${name} ${t('geoSearch.locationNames.subwayStation')}`, street, city]);
          default:
            return this.asName([`${name} ${t('geoSearch.locationNames.genericStation')}`, street, city]);
        }
      case 'highway':
        switch (feature.properties.geocoding.osm_value) {
          // Don't ask why bus_stop is part of highway category.
          case 'bus_stop':
            return this.asName([`${name} ${t('geoSearch.locationNames.busStation')}`, street, city]);
          default:
            return this.asName([`${name} ${t('geoSearch.locationNames.genericStation')}`, street, city]);
        }
      case 'place':
        switch (feature.properties.geocoding.osm_value) {
          case 'house':
          { const { housenumber } = feature.properties.geocoding;
            return this.asName([`${street} ${housenumber}`, city, county]); }
          default:
            return this.asName([street, city]);
        }
      default:
        return this.asName([name, street, city]);
    }
  }

  /**
   * Simply joins all non-null items together in a string, separated by comma
   */
  private static asName(nameComponents: unknown[]): string {
    return nameComponents.filter(Boolean).join(', ');
  }
}
